import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Container, Typography, Stepper, Step, StepLabel, Paper, Grid,
  Card, CardContent, Button, TextField, FormControl, InputLabel, Select,
  MenuItem, Radio, RadioGroup, FormControlLabel, FormLabel, Divider,
  IconButton, Alert, Stack, CircularProgress
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  People as PeopleIcon,
  LocationOn as LocationIcon,
  CreditCard as CreditCardIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import PaymentGateway from '../components/PaymentGateway';
import { formatGBP, getRoomRates, getRoomTypesRoomsManagement, getRoomTaxesByRoomTypeId } from '../services/api';
import Seo from '../components/Seo';

const Reservation = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [availableRoomTypes, setAvailableRoomTypes] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [showPaymentGateway, setShowPaymentGateway] = useState(false);

  // room/search data (from router state or query)
  const roomFromState = location.state?.room;
  const searchData   = location.state?.searchData || {};

  const [reservationData, setReservationData] = useState({
    room: roomFromState || null,
    checkIn: searchParams.get('checkIn') || searchData.checkIn || new Date(Date.now() + 86400000).toISOString().split('T')[0],
    checkOut: searchParams.get('checkOut') || searchData.checkOut || new Date(Date.now() + 172800000).toISOString().split('T')[0],
    adults: parseInt(searchParams.get('adults')) || searchData.adults || 1,
    children: parseInt(searchParams.get('children')) || searchData.children || 0,
    rooms: parseInt(searchParams.get('rooms')) || searchData.rooms || 1,
    selectedRooms: roomFromState ? [{ ...roomFromState, quantity: 1 }] : [],
    guestInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      country: 'United Kingdom',
      specialRequests: ''
    },
    paymentMethod: 'online-payment',
    paymentInfo: {
      cardNumber: '',
      expiryDate: '',
      cvv: '',
      cardholderName: ''
    }
  });

  // If we arrive with a selected plan, ensure Step 1 displays the plan-adjusted nightly price.
  useEffect(() => {
    if (!roomFromState) return;
    setReservationData((prev) => {
      const rooms = Array.isArray(prev.selectedRooms) ? prev.selectedRooms : [];
      if (!rooms.length) return prev;

      const nextRooms = rooms.map((r, idx) => {
        if (idx !== 0) return r;
        const planSurcharge = Number(r?.selectedPlan?.basePrice ?? r?.selectedPlan?.BasePrice ?? 0) || 0;
        if (planSurcharge <= 0) return r;

        const baseNightly = Number(r?.basePriceNightly ?? r?.basePrice ?? r?.price ?? 0) || 0;
        const includesTax = (r?.includesTax ?? r?.IncludesTax) === true;
        const taxRate = Number(r?.taxRate ?? r?.TaxRate ?? 0) || 0;

        const baseWithPlan = baseNightly + planSurcharge;
        const computedTax = includesTax ? 0 : (baseWithPlan * taxRate);
        const withTaxNightly = includesTax ? baseWithPlan : (baseWithPlan + computedTax);

        return {
          ...r,
          basePriceNightly: baseNightly,
          totalWithTaxNightly: withTaxNightly,
        };
      });

      return { ...prev, selectedRooms: nextRooms };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // helpers
  const calculateNights = () => {
    const checkIn  = new Date(reservationData.checkIn);
    const checkOut = new Date(reservationData.checkOut);
    const diffTime = Math.max(0, checkOut - checkIn);
    return Math.max(1, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  };

  const getRoomDisplayPrice = (room) => {
    // Prefer nightly values; fall back to any total that includes tax
    return room?.totalWithTaxNightly
      ?? room?.priceWithTax
      ?? room?.totalWithTax
      ?? room?.basePriceNightly
      ?? room?.basePrice
      ?? room?.price
      ?? 0;
  };

  const availableRoomTypesWithRates = availableRoomTypes.filter((rt) => {
    const price = getRoomDisplayPrice(rt);
    return Number(price || 0) > 0;
  });

  const calculateTotal = () => {
    const nights = calculateNights();
    if (!reservationData.selectedRooms?.length) {
      return { subtotal: 0, taxes: 0, total: 0, nights, taxBreakdown: [] };
    }

    let subtotal = 0;
    let totalWithTax = 0;

    reservationData.selectedRooms.forEach(room => {
      const planSurcharge = Number(room?.selectedPlan?.basePrice ?? room?.selectedPlan?.BasePrice ?? 0) || 0;
      const base = (room.basePriceNightly ?? room.basePrice ?? room.price ?? 0) + planSurcharge;
      const taxRate = Number(room?.taxRate ?? room?.TaxRate ?? 0) || 0;
      const includesTax = (room?.includesTax ?? room?.IncludesTax) === true;
      const computedTax = includesTax ? 0 : (base * taxRate);
      const withTax = includesTax ? base : (base + computedTax);
      const qty = room.quantity || 1;
      subtotal += base * nights * qty;
      totalWithTax += withTax * nights * qty;
    });

    const taxes = Math.max(0, totalWithTax - subtotal);

    const breakdownMap = new Map();
    reservationData.selectedRooms.forEach((room) => {
      if (!room?.taxBreakdown?.length) return;
      const qty = room.quantity || 1;
      room.taxBreakdown.forEach((t) => {
        const key = (t.name || 'Tax').toString();
        const prev = breakdownMap.get(key) || 0;
        breakdownMap.set(key, prev + (Number(t.amount || 0) * nights * qty));
      });
    });

    const taxBreakdown = Array.from(breakdownMap.entries()).map(([name, amount]) => ({ name, amount }));

    return { subtotal, taxes, total: totalWithTax, nights, taxBreakdown };
  };

  // Fetch available room types
  useEffect(() => {
    const fetchRoomTypes = async () => {
      try {
        setLoadingRooms(true);
        const [roomTypesDataRaw, roomRatesDataRaw] = await Promise.all([
          getRoomTypesRoomsManagement(),
          getRoomRates()
        ]);

        const roomTypes = Array.isArray(roomTypesDataRaw)
          ? roomTypesDataRaw
          : (roomTypesDataRaw?.data || []);

        const roomRates = Array.isArray(roomRatesDataRaw)
          ? roomRatesDataRaw
          : (roomRatesDataRaw?.data || []);

        const toDateOnly = (v) => {
          if (!v) return null;
          const d = new Date(v);
          return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
        };

        const isWithin = (date, from, to) => {
          if (!date) return true;
          if (from && date < from) return false;
          if (to && date > to) return false;
          return true;
        };

        const getRateRoomTypeId = (rate) => {
          if (!rate) return null;
          return rate.roomTypeId ?? rate.RoomTypeId ?? rate.roomType?.id ?? rate.roomType?.Id ?? rate.RoomType?.Id ?? null;
        };

        const checkInKey = (searchParams.get('checkIn') || searchData.checkIn || reservationData.checkIn);
        const checkInDate = toDateOnly(checkInKey);

        const computeRoomTax = (basePrice, includesTax, taxesRaw) => {
          const taxes = Array.isArray(taxesRaw) ? taxesRaw : [];
          const breakdown = [];
          let taxTotal = 0;
          let percentSum = 0;

          taxes.forEach((t) => {
            const name = t.taxName ?? t.TaxName ?? 'Tax';
            const type = t.taxType ?? t.TaxType ?? 'Percentage';
            const valueRaw = t.taxValue ?? t.TaxValue ?? 0;
            const value = typeof valueRaw === 'number' ? valueRaw : parseFloat(valueRaw || '0');

            let amount = 0;
            if (includesTax) {
              amount = 0;
            } else if (String(type).toLowerCase() === 'percentage') {
              amount = (Number(basePrice || 0) * Number(value || 0)) / 100;
              percentSum += Number(value || 0);
            } else {
              amount = Number(value || 0);
            }
            taxTotal += amount;
            breakdown.push({ name, type, value, amount });
          });

          const totalWithTax = includesTax ? Number(basePrice || 0) : (Number(basePrice || 0) + taxTotal);
          return { taxTotal, taxBreakdown: breakdown, totalWithTax, percentSum };
        };

        const roomsWithPricing = await Promise.all(roomTypes.map(async (room) => {
          const roomNameKey = (room.roomTypeName || room.name || room.Name || '').toString().toLowerCase();
          const roomTypeId = room.roomTypeId ?? room.RoomTypeId ?? room.id ?? room.Id ?? null;

          const matchingRates = roomRates
            .filter((rate) => {
              const rateRoomTypeId = getRateRoomTypeId(rate);
              if (roomTypeId && rateRoomTypeId) return Number(rateRoomTypeId) === Number(roomTypeId);

              const rateRoomType = rate?.roomType;
              const rateKey = typeof rateRoomType === 'string'
                ? rateRoomType.toLowerCase()
                : (rateRoomType?.roomTypeName || rateRoomType?.name || rate.RoomType?.Name || '').toString().toLowerCase();
              return rateKey && rateKey === roomNameKey;
            })
            .filter((rate) => {
              const from = toDateOnly(rate.effectiveFrom || rate.EffectiveFrom || rate.validFrom);
              const to = toDateOnly(rate.effectiveTo || rate.EffectiveTo || rate.validTo);
              return isWithin(checkInDate, from, to);
            });

          const rateInfo = matchingRates[0];
          const basePrice = rateInfo?.baseRate ?? rateInfo?.BaseRate ?? room.basePrice ?? room.price ?? 0;

          const includesTax = (rateInfo?.includesTax ?? rateInfo?.IncludesTax) === true;
          const rateTaxPctRaw = rateInfo?.taxPercentage ?? rateInfo?.TaxPercentage;
          const taxRate = typeof rateTaxPctRaw === 'number'
            ? (rateTaxPctRaw / 100)
            : (parseFloat(rateTaxPctRaw || '0') / 100);

          let roomTaxPayload = null;
          try {
            if (roomTypeId) {
              roomTaxPayload = await getRoomTaxesByRoomTypeId(roomTypeId);
            }
          } catch (e) {
            roomTaxPayload = null;
          }

          const roomTaxRows = roomTaxPayload?.data ?? roomTaxPayload?.Data ?? roomTaxPayload?.success ? (roomTaxPayload?.data || []) : (roomTaxPayload || []);

          const taxFromRoomTax = computeRoomTax(basePrice, includesTax, roomTaxRows);

          const computedTaxRate = taxFromRoomTax.percentSum > 0
            ? (taxFromRoomTax.percentSum / 100)
            : (taxRate > 0 ? taxRate : 0);
          const fallbackTaxAmount = includesTax ? 0 : (basePrice * computedTaxRate);
          const fallbackTotalWithTax = includesTax ? basePrice : (basePrice + fallbackTaxAmount);

          const taxAmount = taxFromRoomTax.taxTotal > 0 ? taxFromRoomTax.taxTotal : fallbackTaxAmount;
          const totalWithTax = taxFromRoomTax.taxTotal > 0 ? taxFromRoomTax.totalWithTax : fallbackTotalWithTax;

          return {
            ...room,
            id: roomTypeId,
            roomTypeId,
            roomTypeName: room.roomTypeName || room.name || room.Name,
            basePrice,
            price: basePrice,
            includesTax,
            taxRate,
            taxAmount,
            totalWithTax,
            taxBreakdown: (taxFromRoomTax.taxTotal > 0 ? taxFromRoomTax.taxBreakdown : []),
            validFrom: rateInfo?.effectiveFrom ?? rateInfo?.EffectiveFrom ?? null,
            validTo: rateInfo?.effectiveTo ?? rateInfo?.EffectiveTo ?? null,
            rateName: rateInfo?.rateName ?? rateInfo?.RateName ?? room.roomTypeName ?? room.name,
            rateCode: rateInfo?.rateCode ?? rateInfo?.RateCode ?? null,
          };
        }));

        setAvailableRoomTypes(roomsWithPricing);
      } catch (error) {
        console.error('Error fetching room types:', error);
        setAvailableRoomTypes([]);
      } finally {
        setLoadingRooms(false);
      }
    };

    fetchRoomTypes();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // default demo room if none provided
    if (!roomFromState && !reservationData.room && reservationData.selectedRooms.length === 0) {
      setReservationData(prev => ({
        ...prev,
        selectedRooms: []
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (section, field, value) => {
    if (section) {
      setReservationData(prev => ({ ...prev, [section]: { ...prev[section], [field]: value }}));
    } else {
      setReservationData(prev => ({ ...prev, [field]: value }));
    }
  };

  const removeRoom = (roomId) => {
    setReservationData(prev => ({
      ...prev,
      selectedRooms: prev.selectedRooms.filter(r => r.id !== roomId)
    }));
  };

  const updateRoomQuantity = (roomId, quantity) => {
    if (quantity <= 0) return removeRoom(roomId);
    setReservationData(prev => ({
      ...prev,
      selectedRooms: prev.selectedRooms.map(r => r.id === roomId ? { ...r, quantity } : r)
    }));
  };

  const addRoomType = (roomType) => {
    const exists = reservationData.selectedRooms.find(r => r.id === roomType.id);
    if (exists) {
      // Increase quantity if already selected
      updateRoomQuantity(roomType.id, exists.quantity + 1);
    } else {
      // Add new room type
      setReservationData(prev => ({
        ...prev,
        selectedRooms: [...prev.selectedRooms, { ...roomType, quantity: 1 }]
      }));
    }
  };

  const handleNext = () => {
    if (isStepValid()) {
      setStep((prev) => prev + 1);
      // Scroll to top when moving to next step
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      alert('Please complete all required fields before proceeding.');
    }
  };

  const prevStep = () => {
    setStep(s => Math.max(1, s - 1));
    // Scroll to top when going back
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isStepValid = () => {
    if (step === 1) {
      return reservationData.selectedRooms.length > 0 && reservationData.checkIn && reservationData.checkOut;
    }
    if (step === 2) {
      const { firstName, lastName, email, phone } = reservationData.guestInfo;
      const validEmail = /\S+@\S+\.\S+/.test(email);
      const validPhone = phone?.trim().length >= 7;
      return Boolean(firstName && lastName && validEmail && validPhone);
    }
    if (step === 3 && reservationData.paymentMethod === 'credit-card') {
      const { cardNumber, expiryDate, cvv, cardholderName } = reservationData.paymentInfo;
      return cardNumber && expiryDate && cvv && cardholderName;
    }
    return true;
  };

  const handleSubmit = async () => {
    try {
      setProcessingPayment(true);

      // Prepare the API request
      const apiRequest = {
        hotelId: reservationData.hotelId || reservationData.selectedRooms?.[0]?.hotelId || null,
        roomTypeId: reservationData.roomTypeId || reservationData.selectedRooms?.[0]?.roomTypeId || reservationData.selectedRooms?.[0]?.id || null,
        selectedRooms: reservationData.selectedRooms.map(room => ({
          roomTypeId: room.roomTypeId || room.id,
          roomTypeName: room.roomTypeName || room.name,
          quantity: room.quantity || 1,
          planId: room?.selectedPlan?.id ?? room?.selectedPlan?.Id ?? null,
          planName: room?.selectedPlan?.name ?? room?.selectedPlan?.Name ?? null,
          planSurcharge: parseFloat(room?.selectedPlan?.basePrice ?? room?.selectedPlan?.BasePrice ?? 0) || 0,
          basePrice: parseFloat((room.basePriceNightly ?? room.basePrice ?? room.price ?? 0) + (Number(room?.selectedPlan?.basePrice ?? room?.selectedPlan?.BasePrice ?? 0) || 0)),
          taxAmount: parseFloat(((room?.includesTax ?? room?.IncludesTax) === true)
            ? 0
            : (((room.basePriceNightly ?? room.basePrice ?? room.price ?? 0) + (Number(room?.selectedPlan?.basePrice ?? room?.selectedPlan?.BasePrice ?? 0) || 0)) * (Number(room?.taxRate ?? room?.TaxRate ?? 0) || 0))),
          totalWithTax: parseFloat(((room?.includesTax ?? room?.IncludesTax) === true)
            ? ((room.basePriceNightly ?? room.basePrice ?? room.price ?? 0) + (Number(room?.selectedPlan?.basePrice ?? room?.selectedPlan?.BasePrice ?? 0) || 0))
            : (((room.basePriceNightly ?? room.basePrice ?? room.price ?? 0) + (Number(room?.selectedPlan?.basePrice ?? room?.selectedPlan?.BasePrice ?? 0) || 0)) * (1 + (Number(room?.taxRate ?? room?.TaxRate ?? 0) || 0))))
        })),
        checkInDate: reservationData.checkIn,
        checkOutDate: reservationData.checkOut,
        numberOfAdults: parseInt(reservationData.adults) || 1,
        numberOfChildren: parseInt(reservationData.children) || 0,
        specialRequests: reservationData.guestInfo.specialRequests || '',
        firstName: reservationData.guestInfo.firstName || '',
        lastName: reservationData.guestInfo.lastName || '',
        email: reservationData.guestInfo.email || '',
        phoneNumber: reservationData.guestInfo.phone || '',
        address: reservationData.guestInfo.address || '',
        city: reservationData.guestInfo.city || '',
        country: reservationData.guestInfo.country || 'United Kingdom',
        postalCode: reservationData.guestInfo.postalCode || '',
        idType: 'CNIC',
        idNumber: reservationData.guestInfo.idNumber || '',
        paymentMethod: reservationData.paymentMethod || 'pay-at-hotel',
        totalAmount: parseFloat(totals.total) || 0
      };

      console.log('Sending reservation request:', apiRequest);

      // Create reservation first
      const response = await fetch(`${process.env.REACT_APP_API_URL}/CustomerReservation/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiRequest),
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || 'Failed to create reservation');
      }

      const bookingResponse = await response.json();
      const confirmationId = bookingResponse.reservationNumber || bookingResponse.id || `LH${Date.now().toString().slice(-6)}`;

      // Check payment method
      if (reservationData.paymentMethod === 'online-payment') {
        // Process online payment through PayFast
        const paymentRequest = {
          BasketId: confirmationId,
          Amount: parseFloat(totals.total),
          CustomerEmail: reservationData.guestInfo.email,
          CustomerName: `${reservationData.guestInfo.firstName} ${reservationData.guestInfo.lastName}`
        };

        console.log('Sending payment request:', paymentRequest);
        console.log('API URL:', `${process.env.REACT_APP_API_URL}/Payfast/ProcessPayment`);

        const paymentResponse = await fetch(`${process.env.REACT_APP_API_URL}/Payfast/ProcessPayment`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(paymentRequest),
        });

        console.log('Payment response status:', paymentResponse.status);

        if (!paymentResponse.ok) {
          const errorText = await paymentResponse.text();
          console.error('Payment API error:', errorText);
          throw new Error(`Failed to process payment: ${errorText}`);
        }

        const paymentData = await paymentResponse.json();
        console.log('Payment data received:', paymentData);
        
        // Normalize keys from backend: paymentUrl/paymentData → PaymentUrl/PaymentData
        const normalizedPaymentData = {
          PaymentUrl: paymentData.PaymentUrl || paymentData.paymentUrl,
          PaymentData: paymentData.PaymentData || paymentData.paymentData,
          Token: paymentData.Token || paymentData.token,
          BasketId: paymentData.BasketId || paymentData.basketId,
        };

        console.log('Normalized payment data:', normalizedPaymentData);

        if (!normalizedPaymentData.PaymentUrl || !normalizedPaymentData.PaymentData) {
          throw new Error('Invalid payment data received from server');
        }
        
        setPaymentData(normalizedPaymentData);
        setShowPaymentGateway(true);
      } else {
        // For pay at hotel, go directly to thank you page
        navigate('/thank-you', { 
          state: { 
            reservationData: { ...reservationData, totals }, 
            paymentMethod: reservationData.paymentMethod,
            bookingResponse 
          } 
        });
      }
    } catch (error) {
      console.error('Error creating reservation:', error);
      alert(`Failed to create reservation: ${error.message}`);
    } finally {
      setProcessingPayment(false);
    }
  };

  const totals = calculateTotal();
  const steps = ['Select Rooms', 'Guest Details', 'Payment'];

  // --- Key part: column order to match your screenshot ---
  // On Step 2, put SUMMARY on the LEFT and FORM on the RIGHT.
  const orders = step === 2
    ? { main: { xs: 1, lg: 2 }, summary: { xs: 2, lg: 1 } }
    : { main: { xs: 1, lg: 1 }, summary: { xs: 2, lg: 2 } };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <Seo
        title="Reservation"
        description="Reserve your Grosvenor Plymouth stay in minutes. Select dates and guests, choose your room, and confirm your booking securely online."
        keywords="Grosvenor Plymouth reservation, book room, online booking, check-in, check-out, secure reservation"
        author="Grosvenor Plymouth"
      />
      <Box sx={{ 
        position: 'relative',
        pt: { xs: 8, md: 12 },
        pb: { xs: 3, md: 4 },
        background: 'linear-gradient(135deg, rgba(44, 90, 160, 0.95) 0%, rgba(102, 126, 234, 0.9) 100%)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.15,
          zIndex: 0,
        }
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, px: { xs: 1.5, md: 2 } }}>
          {/* Header & Stepper */}
          <Paper elevation={0} sx={{ 
            p: { xs: 2, md: 3 }, 
            borderRadius: '14px', 
            border: '1px solid #e5e7eb', 
            background: '#fff',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
          }}>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 1, fontSize: { xs: '1.5rem', md: '1.75rem' }, letterSpacing: '0.2px' }}>
            Complete Your Reservation
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280', mb: 2.5, fontSize: { xs: '0.85rem', md: '0.95rem' } }}>
            Follow the steps below to secure your booking.
          </Typography>
          <Stepper activeStep={step - 1} connector={<Box sx={{ flex: 1, height: '2px', backgroundColor: '#e5e7eb', mx: 1.5 }} />}>
            {steps.map((label, idx) => (
              <Step key={label}>
                <StepLabel 
                  StepIconComponent={() => (
                    <Box sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: idx < step ? '#2563eb' : '#e5e7eb',
                      color: idx < step ? '#fff' : '#374151',
                      fontSize: '13px',
                      fontWeight: 700,
                      boxShadow: idx < step ? '0 0 0 4px rgba(37, 99, 235, 0.25)' : 'none'
                    }}>
                      {idx + 1}
                    </Box>
                  )}
                  sx={{ '& .MuiStepLabel-label': { display: 'none' } }}
                />
              </Step>
            ))}
          </Stepper>
          </Paper>
        </Container>
      </Box>

      {/* Main Content Area */}
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 }, px: { xs: 1.5, md: 2 } }}>
        <Grid container spacing={{ xs: 2, md: 2.5 }} sx={{ alignItems: 'flex-start' }}>
          {/* SUMMARY column (LEFT) - Only show on Step 2 */}
          {step === 2 && (
            <Grid
              item
              xs={12}
              md={3.8}
              sx={{
                order: { xs: 2, md: 1 },
                display: 'block'
              }}
            >
              <Box sx={{ position: { md: 'sticky' }, top: { md: 88 }, height: '100%' }}>
                <Paper
                  sx={{
                    p: { xs: 2, md: 2.5 },
                    borderRadius: '14px',
                    border: '1px solid #e5e7eb',
                    background: '#fff',
                    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06), 0 1px 3px rgba(15, 23, 42, 0.06)'
                  }}
                >
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 1.25, fontSize: { xs: '1rem', md: '1.125rem' } }}>Booking Summary</Typography>

                  {reservationData.selectedRooms.map(room => (
                    <Box key={room.id} sx={{ mb: 1.75, p: 1.75, border: '1px dashed #e5e7eb', borderRadius: '12px', background: '#fafafa' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="subtitle2" fontWeight={800} sx={{ color: '#0f172a' }}>
                          {room.roomTypeName || room.name || 'Room'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                          {room.quantity} × {calculateNights()} night{calculateNights() > 1 ? 's' : ''}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>Rate (per night)</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                          {formatGBP(getRoomDisplayPrice(room))}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#6b7280' }}>Total for stay</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                          {formatGBP(getRoomDisplayPrice(room) * room.quantity * calculateNights())}
                        </Typography>
                      </Stack>
                    </Box>
                  ))}

                  <Divider sx={{ my: 2 }} />

                  <Stack spacing={1} sx={{ mb: 1.75 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>Check-in</Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ color: '#0f172a' }}>{reservationData.checkIn}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>Check-out</Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ color: '#0f172a' }}>{reservationData.checkOut}</Typography>
                    </Stack>
                  </Stack>

                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>Subtotal</Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ color: '#0f172a' }}>{formatGBP(totals.subtotal || 0)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>Taxes & Fees</Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ color: '#0f172a' }}>{formatGBP(totals.taxes || 0)}</Typography>
                    </Stack>
                    {Array.isArray(totals.taxBreakdown) && totals.taxBreakdown.length > 0 && (
                      <Box sx={{ mt: 0.5 }}>
                        {totals.taxBreakdown.map((t) => (
                          <Stack
                            key={t.name}
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ mt: 0.5 }}
                          >
                            <Typography variant="caption" sx={{ color: '#6b7280' }}>{t.name}</Typography>
                            <Typography variant="caption" fontWeight={700} sx={{ color: '#0f172a' }}>
                              {formatGBP(Number(t.amount || 0))}
                            </Typography>
                          </Stack>
                        ))}
                      </Box>
                    )}
                    
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ 
                        mt: 1.75,
                        p: 1.25, 
                        borderRadius: '999px', 
                        background: '#f1f5ff',
                        border: '1px solid #dbe5ff',
                        color: '#0b1b56'
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={700}>Total</Typography>
                      <Typography variant="subtitle1" fontWeight={700}>{formatGBP(totals.total || 0)}</Typography>
                    </Stack>
                  </Stack>
                  <Typography variant="caption" sx={{ display: 'block', color: '#6b7280', mt: 1.25, fontSize: '13px' }}>
                    You can review your details on the next step before payment.
                  </Typography>
                </Paper>
              </Box>
            </Grid>
          )}

          {/* MAIN column */}
          <Grid 
            item 
            xs={12} 
            md={step === 1 || step === 3 ? 12 : 8.2} 
            sx={{ 
              order: { xs: 1, md: step === 1 ? 1 : 2 },
              display: 'flex', 
              justifyContent: step === 1 ? 'center' : 'flex-end' 
            }}
          >
            {/* Step 1 */}
            {step === 1 && (
              <Box sx={{ width: '100%' }}>
                <Typography variant="h5" fontWeight={800} sx={{ mb: 3, fontSize: { xs: '1.25rem', md: '1.5rem' } }}>Select Your Rooms</Typography>

                <Paper sx={{ p: { xs: 1.5, md: 2 }, mb: 3, borderRadius: 2, backgroundColor: '#f9fafb', border: '1px dashed #e5e7eb' }}>
                  <Grid container spacing={{ xs: 1.5, md: 2 }}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CalendarIcon fontSize="small" color="primary" />
                        <Typography fontWeight={600} variant="body2" sx={{ fontSize: { xs: '0.85rem', md: '0.95rem' } }}>
                          {reservationData.checkIn} → {reservationData.checkOut}
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PeopleIcon fontSize="small" color="primary" />
                        <Typography fontWeight={600} variant="body2" sx={{ fontSize: { xs: '0.85rem', md: '0.95rem' } }}>
                          {reservationData.adults} Adults, {reservationData.children} Children
                        </Typography>
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <LocationIcon fontSize="small" color="primary" />
                        <Typography fontWeight={600} variant="body2">
                          {reservationData.rooms} Room{reservationData.rooms > 1 ? 's' : ''}
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                </Paper>

                <Grid container spacing={3}>
                  {/* LEFT: Available Room Categories */}
                  <Grid item xs={12} md={4.5} sx={{ alignSelf: 'flex-start' }}>
                    <Paper sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e5e7eb', background: '#fff', height: '100%' }}>
                      <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: '#111827' }}>
                        Available Room Categories
                      </Typography>
                      {loadingRooms ? (
                        <Typography variant="body2" sx={{ color: '#6b7280', textAlign: 'center', py: 4 }}>
                          Loading room types...
                        </Typography>
                      ) : (
                        <Stack spacing={1.5}>
                          {availableRoomTypes.filter(roomType => getRoomDisplayPrice(roomType) > 0).map(roomType => {
                            const isSelected = reservationData.selectedRooms.some(r => r.id === roomType.id);
                            return (
                              <Card 
                                key={roomType.id}
                                variant="outlined" 
                                sx={{ 
                                  borderRadius: 2,
                                  border: isSelected ? '2px solid #2563eb' : '1px solid #e5e7eb',
                                  backgroundColor: isSelected ? '#eff6ff' : '#fff',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  '&:hover': {
                                    borderColor: '#2563eb',
                                    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.15)',
                                  }
                                }}
                                onClick={() => addRoomType(roomType)}
                              >
                                  <CardContent sx={{ p: 2 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                      <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#111827', mb: 0.5 }}>
                                          {roomType.roomTypeName}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 1 }}>
                                          {roomType.description || 'Comfortable accommodation'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#2563eb', fontWeight: 700 }}>
                                          {formatGBP(getRoomDisplayPrice(roomType))} / night
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                          Max {roomType.maxOccupancy || 2} guests
                                        </Typography>
                                      </Box>
                                      <IconButton 
                                        size="small" 
                                        sx={{ 
                                          backgroundColor: isSelected ? '#2563eb' : '#f3f4f6',
                                          color: isSelected ? '#fff' : '#6b7280',
                                          '&:hover': {
                                            backgroundColor: isSelected ? '#1d4ed8' : '#e5e7eb',
                                          }
                                        }}
                                      >
                                        <AddIcon fontSize="small" />
                                      </IconButton>
                                    </Stack>
                                  </CardContent>
                                </Card>
                            );
                          })}
                        </Stack>
                      )}
                    </Paper>
                  </Grid>

                  {/* RIGHT: Selected Rooms */}
                  <Grid item xs={12} md={7.5} sx={{ alignSelf: 'flex-start' }}>
                    <Paper sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e5e7eb', background: '#fff', minHeight: 400 }}>
                      <Typography variant="h6" fontWeight={700} sx={{ mb: 2, color: '#111827' }}>
                        Selected Rooms ({reservationData.selectedRooms.length})
                      </Typography>
                      
                      {reservationData.selectedRooms.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                          <Typography variant="body1" sx={{ color: '#9ca3af', mb: 1 }}>
                            No rooms selected yet
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>
                            Click on room categories from the left to add them
                          </Typography>
                        </Box>
                      ) : (
                        <Stack spacing={2}>
                          {reservationData.selectedRooms.map(room => (
                            <Card key={room.id} variant="outlined" sx={{ borderRadius: 2, border: '2px solid #e5e7eb' }}>
                              <CardContent sx={{ p: 2.5 }}>
                                <Grid container spacing={2} alignItems="center">
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="h6" fontWeight={800} sx={{ color: '#111827' }}>
                                      {room.roomTypeName || room.name || 'Room'}
                                    </Typography>
                                    <Typography sx={{ color: '#2563eb', fontWeight: 700, mb: 0.5 }}>
                                      {formatGBP(getRoomDisplayPrice(room))} / night
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                      Total: {formatGBP(getRoomDisplayPrice(room) * room.quantity * calculateNights())}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} md={6} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                                    <Stack direction="row" spacing={2} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} alignItems="center">
                                      <Stack direction="row" spacing={1} alignItems="center" sx={{ 
                                        border: '1px solid #e5e7eb', 
                                        borderRadius: 2, 
                                        p: 0.5 
                                      }}>
                                        <IconButton size="small" onClick={() => updateRoomQuantity(room.id, room.quantity - 1)}>
                                          <RemoveIcon fontSize="small" />
                                        </IconButton>
                                        <Typography fontWeight={700} sx={{ minWidth: 32, textAlign: 'center' }}>
                                          {room.quantity}
                                        </Typography>
                                        <IconButton size="small" onClick={() => updateRoomQuantity(room.id, room.quantity + 1)}>
                                          <AddIcon fontSize="small" />
                                        </IconButton>
                                      </Stack>
                                      <IconButton 
                                        color="error" 
                                        onClick={() => removeRoom(room.id)}
                                        sx={{ 
                                          border: '1px solid #fee2e2',
                                          backgroundColor: '#fef2f2',
                                          '&:hover': {
                                            backgroundColor: '#fee2e2',
                                          }
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Stack>
                                  </Grid>
                                </Grid>
                              </CardContent>
                            </Card>
                          ))}
                        </Stack>
                      )}

                      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button 
                          variant="contained" 
                          size="large" 
                          onClick={handleNext} 
                          disabled={!isStepValid()} 
                          sx={{ 
                            borderRadius: 2, 
                            fontWeight: 700,
                            px: 4,
                            py: 1.5,
                            backgroundColor: '#2563eb',
                            '&:hover': {
                              backgroundColor: '#1d4ed8',
                            }
                          }}
                        >
                          Continue to Guest Details →
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Step 2 — Guest info (RIGHT on desktop) */}
            {step === 2 && (
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: '14px',
                  border: '1px solid #e5e7eb',
                  background: '#fff',
                  boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06), 0 1px 3px rgba(15, 23, 42, 0.06)',
                  maxWidth: '600px',
                  width: '100%'
                }}
              >
                <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5, fontSize: '18px' }}>Guest Information</Typography>
                <Grid container spacing={1.5}>
                  {/* Row 1: First Name, Last Name (2 fields) */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#6b7280', fontSize: '13px' }}>
                      First Name <Box component="span" sx={{ color: '#ef4444' }}>*</Box>
                    </Typography>
                    <TextField 
                      fullWidth 
                      size="small"
                      value={reservationData.guestInfo.firstName}
                      onChange={(e) => handleInputChange('guestInfo', 'firstName', e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputBase-input': { py: 0.75 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#6b7280', fontSize: '13px' }}>
                      Last Name <Box component="span" sx={{ color: '#ef4444' }}>*</Box>
                    </Typography>
                    <TextField 
                      fullWidth 
                      size="small"
                      value={reservationData.guestInfo.lastName}
                      onChange={(e) => handleInputChange('guestInfo', 'lastName', e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputBase-input': { py: 0.75 } }}
                    />
                  </Grid>
                  
                  {/* Row 2: Email, Phone (2 fields) */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#6b7280', fontSize: '13px' }}>
                      Email Address <Box component="span" sx={{ color: '#ef4444' }}>*</Box>
                    </Typography>
                    <TextField 
                      fullWidth 
                      type="email" 
                      size="small"
                      value={reservationData.guestInfo.email}
                      onChange={(e) => handleInputChange('guestInfo', 'email', e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputBase-input': { py: 0.75 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#6b7280', fontSize: '13px' }}>
                      Phone Number <Box component="span" sx={{ color: '#ef4444' }}>*</Box>
                    </Typography>
                    <TextField 
                      fullWidth 
                      type="tel" 
                      size="small"
                      placeholder="+92 3XX XXXXXXX"
                      value={reservationData.guestInfo.phone}
                      onChange={(e) => handleInputChange('guestInfo', 'phone', e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputBase-input': { py: 0.75 } }}
                    />
                  </Grid>
                  
                  {/* Row 3: Address, City (2 fields) */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#6b7280', fontSize: '13px' }}>
                      Address
                    </Typography>
                    <TextField 
                      fullWidth 
                      size="small"
                      value={reservationData.guestInfo.address}
                      onChange={(e) => handleInputChange('guestInfo', 'address', e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputBase-input': { py: 0.75 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#6b7280', fontSize: '13px' }}>
                      City
                    </Typography>
                    <TextField 
                      fullWidth 
                      size="small"
                      value={reservationData.guestInfo.city}
                      onChange={(e) => handleInputChange('guestInfo', 'city', e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputBase-input': { py: 0.75 } }}
                    />
                  </Grid>
                  
                  {/* Row 4: Country, Postal Code (2 fields) */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#6b7280', fontSize: '13px' }}>
                      Country
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={reservationData.guestInfo.country}
                        onChange={(e) => handleInputChange('guestInfo', 'country', e.target.value)}
                        sx={{ borderRadius: '12px', '& .MuiSelect-select': { py: 0.75 } }}
                      >
                        <MenuItem value="United Kingdom">United Kingdom</MenuItem>
                        <MenuItem value="India">India</MenuItem>
                        <MenuItem value="Bangladesh">Bangladesh</MenuItem>
                        <MenuItem value="United Arab Emirates">United Arab Emirates</MenuItem>
                        <MenuItem value="Saudi Arabia">Saudi Arabia</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#6b7280', fontSize: '13px' }}>
                      Postal Code <Box component="span" sx={{ fontSize: '12px', color: '#9ca3af' }}>(optional)</Box>
                    </Typography>
                    <TextField 
                      fullWidth 
                      size="small"
                      value={reservationData.guestInfo.postalCode || ''}
                      onChange={(e) => handleInputChange('guestInfo', 'postalCode', e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputBase-input': { py: 0.75 } }}
                    />
                  </Grid>
                  
                  {/* Row 5: Special Requests (full width) */}
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#6b7280', fontSize: '13px' }}>
                      Special Requests <Box component="span" sx={{ fontSize: '12px', color: '#9ca3af' }}>(optional)</Box>
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Allergies, etc."
                      value={reservationData.guestInfo.specialRequests}
                      onChange={(e) => handleInputChange('guestInfo', 'specialRequests', e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' }, '& .MuiInputBase-input': { py: 0.75 } }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', mt: 1.5 }}>
                      <Button 
                        variant="outlined" 
                        onClick={prevStep} 
                        sx={{ 
                          borderRadius: '999px', 
                          fontWeight: 600,
                          px: 2,
                          py: 1.5,
                          borderColor: '#e5e7eb',
                          color: '#1e3a8a',
                          background: '#eef2ff',
                          '&:hover': { borderColor: '#d1d5db', background: '#e0e7ff' }
                        }}
                      >
                        ← Back to Rooms
                      </Button>
                      <Button 
                        variant="contained" 
                        disabled={!isStepValid()} 
                        onClick={handleNext} 
                        sx={{ 
                          borderRadius: '999px', 
                          fontWeight: 600,
                          px: 2,
                          py: 1.5,
                          background: '#2563eb',
                          '&:hover': { background: '#1d4ed8' },
                          '&:disabled': { background: '#cbd5e1', color: '#64748b' }
                        }}
                      >
                        Continue to Payment →
                      </Button>
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', color: '#6b7280', mt: 1.25, fontSize: '13px' }}>
                      You can review your details on the next step before payment.
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* Step 3 - Payment with Booking Summary on Left */}
            {step === 3 && (
              <Box sx={{ width: '100%', maxWidth: '1200px' }}>
                <Grid container spacing={3}>
                  {/* LEFT: Booking Summary */}
                  <Grid item xs={12} md={5} sx={{ order: { xs: 1, md: 1 } }}>
                    <Paper sx={{ p: 2.75, borderRadius: 3, border: '1px solid #e5e7eb', background: '#fff', position: 'sticky', top: 100 }}>
                      <Typography variant="h6" fontWeight={800} sx={{ mb: 2, fontSize: '17px' }}>Booking Summary</Typography>
                      
                      {reservationData.selectedRooms.map(room => (
                        <Box key={room.id} sx={{ mb: 1.5, pb: 1.5, borderBottom: '1px dashed #e5e7eb' }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" fontWeight={700} sx={{ color: '#0f172a' }}>
                              {room.roomTypeName || room.name}
                            </Typography>
                            <Typography variant="body2" fontWeight={700} sx={{ color: '#0f172a' }}>
                              {formatGBP(getRoomDisplayPrice(room))} / night
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" sx={{ color: '#6b7280' }}>
                              {room.quantity} × {calculateNights()} night{calculateNights() > 1 ? 's' : ''}
                            </Typography>
                            <Typography variant="caption" fontWeight={700} sx={{ color: '#0f172a' }}>
                              Total: {formatGBP(getRoomDisplayPrice(room) * room.quantity * calculateNights())}
                            </Typography>
                          </Stack>
                        </Box>
                      ))}

                      <Stack spacing={0.75} sx={{ mb: 1.5 }}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>Check-in</Typography>
                          <Typography variant="caption" fontWeight={600}>{reservationData.checkIn}</Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>Check-out</Typography>
                          <Typography variant="caption" fontWeight={600}>{reservationData.checkOut}</Typography>
                        </Stack>
                      </Stack>

                      <Divider sx={{ my: 1.5 }} />

                      <Stack spacing={0.75}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>Subtotal</Typography>
                          <Typography variant="body2" fontWeight={600}>{formatGBP(totals.subtotal || 0)}</Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" sx={{ color: '#6b7280' }}>Taxes & Fees</Typography>
                          <Typography variant="body2" fontWeight={600}>{formatGBP(totals.taxes || 0)}</Typography>
                        </Stack>
                        
                        <Stack 
                          direction="row" 
                          justifyContent="space-between" 
                          sx={{ 
                            mt: 1.5,
                            p: 1.25, 
                            borderRadius: 2, 
                            background: '#dbeafe',
                            border: '1px solid #93c5fd'
                          }}
                        >
                          <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#1e40af' }}>Total</Typography>
                          <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#1e40af' }}>{formatGBP(totals.total || 0)}</Typography>
                        </Stack>
                      </Stack>
                    </Paper>
                  </Grid>

                  {/* RIGHT: Payment Information */}
                  <Grid item xs={12} md={7} sx={{ order: { xs: 2, md: 2 } }}>
                    <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e5e7eb', background: '#fff' }}>
                      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
                        <Box sx={{ width: 34, height: 34, borderRadius: 2, background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <PaymentIcon fontSize="small" sx={{ color: '#4338ca' }} />
                        </Box>
                        <Box>
                          <Typography variant="h6" fontWeight={800}>Payment Information</Typography>
                          <Typography variant="caption" sx={{ color: '#6b7280' }}>Choose your preferred payment method and confirm.</Typography>
                        </Box>
                      </Stack>

                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#374151' }}>Payment Method</Typography>
                        <RadioGroup
                          value={reservationData.paymentMethod}
                          onChange={(e) => handleInputChange(null, 'paymentMethod', e.target.value)}
                        >
                          <FormControlLabel 
                            value="online-payment" 
                            control={<Radio />} 
                            label={<Stack direction="row" spacing={1} alignItems="center"><PaymentIcon fontSize="small" /><span>Online Payment</span></Stack>} 
                          />
                          <FormControlLabel 
                            value="credit-card" 
                            control={<Radio />} 
                            label={<Stack direction="row" spacing={1} alignItems="center"><CreditCardIcon fontSize="small" /><span>Credit Card</span></Stack>} 
                          />
                          <FormControlLabel value="manual" control={<Radio />} label="Pay at Hotel" />
                        </RadioGroup>
                      </Box>

                      {reservationData.paymentMethod === 'credit-card' && (
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#6b7280' }}>Cardholder Name</Typography>
                            <TextField 
                              fullWidth 
                              size="small"
                              placeholder="John Doe"
                              value={reservationData.paymentInfo.cardholderName}
                              onChange={(e) => handleInputChange('paymentInfo', 'cardholderName', e.target.value)}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#6b7280' }}>Card Number</Typography>
                            <TextField 
                              fullWidth 
                              size="small"
                              placeholder="1234 5678 9012 3456"
                              value={reservationData.paymentInfo.cardNumber}
                              onChange={(e) => handleInputChange('paymentInfo', 'cardNumber', e.target.value)}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#6b7280' }}>Expiry Date</Typography>
                            <TextField 
                              fullWidth 
                              size="small"
                              placeholder="MM/YY"
                              value={reservationData.paymentInfo.expiryDate}
                              onChange={(e) => handleInputChange('paymentInfo', 'expiryDate', e.target.value)}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#6b7280' }}>CVV</Typography>
                            <TextField 
                              fullWidth 
                              size="small"
                              placeholder="123"
                              value={reservationData.paymentInfo.cvv}
                              onChange={(e) => handleInputChange('paymentInfo', 'cvv', e.target.value)}
                              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                            />
                          </Grid>
                        </Grid>
                      )}

                      {reservationData.paymentMethod === 'online-payment' && (
                        <Alert severity="info" icon={<PaymentIcon />} sx={{ borderRadius: 2 }}>
                          You will be redirected to our secure payment gateway to complete your payment online.
                        </Alert>
                      )}

                      {reservationData.paymentMethod === 'manual' && (
                        <Alert severity="info" icon={<InfoIcon />} sx={{ borderRadius: 2 }}>
                          You can pay at the hotel during check-in. We accept cash, credit cards, and bank transfers.
                        </Alert>
                      )}

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                        <Button 
                          variant="outlined" 
                          onClick={prevStep} 
                          sx={{ 
                            borderRadius: '999px', 
                            fontWeight: 600,
                            px: 3,
                            borderColor: '#e5e7eb',
                            color: '#1e3a8a',
                            '&:hover': { borderColor: '#d1d5db', background: '#f9fafb' }
                          }}
                        >
                          ← Back
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleSubmit}
                          disabled={processingPayment}
                          sx={{
                            borderRadius: '999px', 
                            fontWeight: 700,
                            px: 4,
                            background: 'linear-gradient(135deg,#10b981 0%,#059669 100%)',
                            '&:hover': { background: 'linear-gradient(135deg,#059669 0%,#047857 100%)' },
                            '&:disabled': { background: '#cbd5e1', color: '#64748b' }
                          }}
                        >
                          {processingPayment ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <CircularProgress size={16} color="inherit" />
                              <span>Processing...</span>
                            </Stack>
                          ) : (
                            <>
                              {reservationData.paymentMethod === 'online-payment' ? '💳 Pay Online' : 
                               reservationData.paymentMethod === 'credit-card' ? '🔒 Complete Payment' : 
                               '✅ Confirm Booking'}
                            </>
                          )}
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>

                </Grid>
              </Box>
            )}

            {/* Payment Gateway Modal */}
            {showPaymentGateway && paymentData && (
              <Box sx={{ 
                position: 'fixed', 
                top: 0, 
                left: 0, 
                right: 0, 
                bottom: 0, 
                backgroundColor: 'rgba(0, 0, 0, 0.8)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                zIndex: 9999 
              }}>
                <Paper sx={{ 
                  p: 4, 
                  borderRadius: 3, 
                  maxWidth: 500, 
                  width: '90%', 
                  textAlign: 'center' 
                }}>
                  <PaymentGateway 
                    paymentData={paymentData}
                    onSuccess={() => {
                      setShowPaymentGateway(false);
                      navigate('/thank-you', { 
                        state: { 
                          reservationData, 
                          paymentMethod: reservationData.paymentMethod,
                          paymentStatus: 'success'
                        } 
                      });
                    }}
                    onError={(error) => {
                      setShowPaymentGateway(false);
                      alert(`Payment failed: ${error}`);
                    }}
                  />
                </Paper>
              </Box>
            )}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Reservation;
